import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNotificationSummary } from "../hooks/useNotificationSummary";
import { mobileTabsFor, navItemsFor, activeNavItem, badgeText } from "../lib/nav";

// Short names for the tabs: a phone gives each about 75px.
const TAB_LABELS = {
  "/dashboard": { labelKey: "nav.tabs.dashboard" },
  "/transactions": { labelKey: "nav.tabs.screenings" },
  "/transactions/new": { labelKey: "nav.tabs.new" },
  "/document-tracking": { labelKey: "nav.tabs.documents" },
};

const Dot = ({ count }) => (
  <span
    className="absolute -top-1.5 left-1/2 ml-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold bg-amber-500 text-slate-950 ring-2 ring-white dark:ring-slate-900"
    aria-hidden="true"
  >
    {badgeText(count)}
  </span>
);

/**
 * The phone's bottom bar (under md): four pages and "More", which opens the
 * full menu. "More" carries the count of anything waiting on a page that is
 * not one of the four.
 */
const MobileTabBar = ({ onOpenMore, moreOpen }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { badges } = useNotificationSummary();
  const tabs = useMemo(() => mobileTabsFor(user), [user]);
  const all = useMemo(() => navItemsFor(user), [user]);
  const active = activeNavItem(pathname, all);
  const onTab = active && tabs.some((tab) => tab.to === active.to);
  const tabPaths = new Set(tabs.map((tab) => tab.to));
  const moreCount = Object.entries(badges).reduce((n, [to, c]) => (tabPaths.has(to) ? n : n + c), 0);

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t"
      style={{ borderColor: "var(--c-border)", paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label={t("nav.tabBar")}
      data-testid="mobile-tab-bar"
    >
      <ul className="grid grid-cols-5 h-16">
        {tabs.map((tab) => {
          const isActive = active?.to === tab.to;
          const isNew = tab.to === "/transactions/new";
          const count = badges[tab.to] || 0;
          const label = t(TAB_LABELS[tab.to]?.labelKey || tab.labelKey);
          return (
            <li key={tab.to} className="min-w-0">
              <NavLink
                to={tab.to}
                end
                aria-label={t(tab.labelKey)}
                aria-current={isActive ? "page" : undefined}
                className={() =>
                  `h-full flex flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium transition-colors ${
                    isActive ? "text-cyan-700 dark:text-cyan-400" : "text-slate-500 dark:text-slate-400 active:text-slate-900"
                  }`
                }
                data-testid={`tab-${tab.to.slice(1).replace(/\//g, "-")}`}
              >
                {isNew ? (
                  <span className={`w-9 h-9 -mt-1 rounded-full flex items-center justify-center text-white shadow-md ${isActive ? "bg-cyan-700" : "bg-cyan-600"}`}>
                    <Plus className="w-5 h-5" aria-hidden="true" />
                  </span>
                ) : (
                  <span className="relative">
                    <tab.icon className="w-6 h-6" aria-hidden="true" />
                    {count > 0 && <Dot count={count} />}
                  </span>
                )}
                <span className="max-w-full truncate leading-none">{label}</span>
              </NavLink>
            </li>
          );
        })}
        <li className="min-w-0">
          <button
            type="button"
            onClick={onOpenMore}
            aria-expanded={moreOpen}
            aria-controls="app-sidebar"
            className={`w-full h-full flex flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium transition-colors ${
              !onTab && active ? "text-cyan-700 dark:text-cyan-400" : "text-slate-500 dark:text-slate-400"
            }`}
            data-testid="tab-more"
          >
            <span className="relative">
              <Menu className="w-6 h-6" aria-hidden="true" />
              {moreCount > 0 && <Dot count={moreCount} />}
            </span>
            <span className="max-w-full truncate leading-none">{t("nav.tabs.more")}</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default MobileTabBar;
